import { createContext, useContext, useMemo } from 'react';
import { identity } from '../utils/fn.js';

const SKIP_RECORDING = 'SKIP_RECORDING';
/** React context which provides the `useInteraction` hook. */
const InteractionHandlerContext = createContext(null);
function useIsRecordingOrPlaybackEnabled() {
    return !!useContext(InteractionHandlerContext);
}
/**
 * Intended to wrap event handlers so that they can be hooked into via the
 * `InteractionHandlerContext`. In Figma, this is used to make our event handlers recordable.
 *
 * @note Switching from an undefined handler to a defined handler (or vice versa) is not supported!
 * Doing so will either cause handlers not to be registered, or stale handlers to remain registered.
 * To conditionally register handlers, you can conditionally pass the recordingKey instead.
 */
function useInteractionHandler(handler, options, dependencies) {
    const useInteraction = useContext(InteractionHandlerContext) || identity;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizeHandler = useMemo(() => handler, dependencies);
    const wrappedInteraction = useInteraction(memoizeHandler, options);
    return handler ? wrappedInteraction : handler;
}
function joinKeys(key, ...keys) {
    if (!isKeyValid(key))
        return undefined;
    let recordingKey = String(key);
    for (const k of keys) {
        if (!isKeyValid(k))
            return undefined;
        recordingKey += `.${k}`;
    }
    return recordingKey;
}
function isKeyValid(key) {
    return key != null && key !== SKIP_RECORDING;
}

export { InteractionHandlerContext, SKIP_RECORDING, joinKeys, useInteractionHandler, useIsRecordingOrPlaybackEnabled };
//# sourceMappingURL=use-interaction-handler.js.map
